import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const HOME = { client: "/client", driver: "/driver", employer: "/employer" };
const LIGHT_TOGGLE = "text-slate-500 hover:text-slate-800 focus-visible:text-indigo-600";
const FIELDS = [
  { key: "current", label: "Current password", testId: "change-current-password-input" },
  { key: "next", label: "New password", testId: "change-new-password-input" },
  { key: "confirm", label: "Confirm new password", testId: "change-confirm-password-input" },
];

export default function ChangePasswordPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return toast.error("New passwords do not match");
    if (form.next.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      const { data } = await apiClient.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      localStorage.setItem("token", data.token);
      const updated = { ...user, must_change_password: false };
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
      toast.success("Password updated");
      navigate(HOME[user.role] || "/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-slate-200 shadow-lg" data-testid="change-password-card">
        <CardHeader className="space-y-2">
          <div className="w-12 h-12 rounded-xl bg-indigo-900 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-lime-400" />
          </div>
          <CardTitle className="text-2xl font-heading font-bold text-slate-900">
            {forced ? "Choose your own password" : "Change password"}
          </CardTitle>
          <CardDescription className="text-slate-600">
            {forced
              ? "For your security, replace the starter password your dispatcher gave you before continuing."
              : "Enter your current password, then pick a new one."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label className="text-slate-700">{f.label}</Label>
                <PasswordInput
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  required
                  data-testid={f.testId}
                  className="h-12 bg-slate-50 border-slate-300"
                  toggleClassName={LIGHT_TOGGLE}
                />
              </div>
            ))}
            <Button type="submit" disabled={saving} className="w-full h-12 bg-indigo-900 hover:bg-indigo-800" data-testid="change-password-submit-btn">
              {saving ? "Saving..." : "Save new password"}
            </Button>
            <button
              type="button"
              onClick={() => { logout(); navigate("/login"); }}
              className="w-full text-sm text-slate-500 hover:text-slate-800 transition-colors"
              data-testid="change-password-signout-btn"
            >
              Sign out
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
